export const LIMITS = {
    username: { min: 3, max: 32 },
    displayName: { min: 1, max: 48 },
    password: { min: 8, max: 128 },
    guildName: { min: 2, max: 64 },
    channelName: { min: 1, max: 48 },
    categoryName: { min: 1, max: 48 },
    roleName: { min: 1, max: 48 },
    messageContent: { max: 4000 },
    topic: { max: 512 },
    description: { max: 512 },
    customStatus: { max: 96 },
    /*
      Quantas pessoas cabem numa sala de voz.
  
      O teto não é uma opinião sobre o tamanho certo de uma conversa: é só o
      ponto a partir do qual um número digitado deixa de ser plausível e passa a
      ser engano de digitação. Quem quiser uma sala grande consegue; a conta que
      importa de verdade, a de banda, aparece no painel e não aqui.
    */
    voiceUserLimit: { min: 0, max: 500 },
    /*
      Sons da bandeja.
  
      Um efeito sonoro de chamada dura poucos segundos; meio megabyte já cobre isso
      com folga em qualquer taxa razoável. O limite existe menos por espaço em
      disco e mais pelo que o arquivo é: quem precisa de mais do que isso está
      tentando tocar uma música inteira, e aí o lugar é outro.
    */
    soundName: { min: 1, max: 48 },
    soundSizeBytes: 512 * 1024,
    soundsPerUser: 24,
    attachmentsPerMessage: 10,
    attachmentSizeBytes: 25 * 1024 * 1024,
    avatarSizeBytes: 4 * 1024 * 1024,
    messagePageSize: 50,
    maxMessagePageSize: 100,
};
export const RATE_LIMITS = {
    /** Mensagens por canal, por usuário. */
    sendMessage: { points: 5, durationSec: 5 },
    editMessage: { points: 5, durationSec: 5 },
    createChannel: { points: 5, durationSec: 30 },
    createGuild: { points: 3, durationSec: 300 },
    login: { points: 10, durationSec: 300 },
    register: { points: 5, durationSec: 3600 },
    uploadFile: { points: 10, durationSec: 60 },
    /** Tocar som na chamada: o suficiente para brincar, pouco para atormentar. */
    playSound: { points: 4, durationSec: 10 },
    /** Frames por conexão de gateway. */
    gatewayFrames: { points: 120, durationSec: 60 },
    typing: { points: 5, durationSec: 10 },
};
/**
 * Teto padrão de mídia.
 *
 * É um teto, não uma sugestão de uso: define até onde alguém pode ir, enquanto o
 * preset escolhido na hora de transmitir decide o que realmente sai. O padrão de
 * uso continua sendo 720p a 30 quadros; 1080p fica disponível para quem quiser e
 * tiver banda.
 *
 * O número saiu de medição, não de estimativa. Em 720p30 com movimento
 * constante, um teto de 1,5 Mbps derrubava a imagem: o VP8 caía para 640x360 e o
 * H264 para 960x540, ambos relatando limitação por banda e nenhuma por
 * processador. O VP9 segurava a resolução gastando toda a folga em compressão, o
 * que aparece como imagem mole e travada. 720p pede perto de 3 Mbps para se
 * sustentar, e 1080p perto de 6.
 *
 * O custo do topo da escala: numa sala de dez pessoas, uma transmissão em 1080p
 * são cerca de 54 Mbps de saída no SFU, ou 24 GB por hora. O painel de
 * administração mostra essa conta contra a franquia do mês e permite baixar o
 * teto a qualquer momento.
 */
export const DEFAULT_VOICE_LIMITS = {
    screenShare: {
        maxWidth: 1920,
        maxHeight: 1080,
        // Acompanha o perfil mais alto disponível: um teto abaixo dele faria a
        // escolha do perfil não valer nada, cortando por dentro sem avisar.
        maxFramerate: 60,
        maxBitrate: 8_000_000,
    },
    camera: {
        maxWidth: 1280,
        maxHeight: 720,
        maxFramerate: 24,
        maxBitrate: 1_200_000,
    },
    audio: {
        maxBitrate: 64_000,
    },
};
/**
 * Perfil econômico, usado quando a cota mensal de banda passa do limiar de
 * alerta. É o único lugar onde a taxa de quadros cai, e por um motivo claro:
 * aqui a escolha é entre degradar ou ficar sem serviço no fim do mês.
 */
export const CONSERVATIVE_VOICE_LIMITS = {
    screenShare: {
        maxWidth: 1280,
        maxHeight: 720,
        maxFramerate: 15,
        maxBitrate: 800_000,
    },
    camera: {
        maxWidth: 640,
        maxHeight: 360,
        maxFramerate: 20,
        maxBitrate: 400_000,
    },
    audio: {
        maxBitrate: 48_000,
    },
};
export const SCREEN_SHARE_PRESETS = [
    {
        id: 'gamer',
        label: 'Gamer',
        hint: '1080p a 60 fps. Para jogo, onde a fluidez conta mais que tudo.',
        width: 1920,
        height: 1080,
        framerate: 60,
        /*
          Dobrar os quadros não dobra a conta.
    
          Quadros seguidos de um jogo se parecem muito entre si, e o codificador
          gasta pouco em cada repetição. Oito contra os seis da alta definição dá a
          folga que os sessenta quadros pedem sem multiplicar a saída por dois.
        */
        bitrate: 8_000_000,
    },
    {
        id: 'sharp',
        label: 'Alta definição',
        hint: '1080p a 30 fps. Pede uma conexão boa dos dois lados.',
        width: 1920,
        height: 1080,
        framerate: 30,
        bitrate: 6_000_000,
    },
    {
        id: 'smooth',
        label: 'Movimento suave',
        hint: '720p a 30 fps. Melhor para vídeo e jogos.',
        width: 1280,
        height: 720,
        framerate: 30,
        bitrate: 3_000_000,
    },
    {
        id: 'economy',
        label: 'Econômico',
        hint: '540p a 30 fps. Menor consumo de dados.',
        width: 960,
        height: 540,
        framerate: 30,
        bitrate: 1_500_000,
    },
];
export const GATEWAY = {
    heartbeatIntervalMs: 30_000,
    /** Margem antes de considerar o cliente morto. */
    heartbeatToleranceMs: 10_000,
    authTimeoutMs: 15_000,
    /** Quantos eventos guardamos por sessão para permitir retomada. */
    replayBufferSize: 256,
    /** Janela para retomar a sessão depois da queda. */
    sessionResumeWindowMs: 120_000,
    typingTimeoutMs: 8_000,
};
/** Cota mensal de saída da Oracle Always Free. */
export const DEFAULT_MONTHLY_EGRESS_QUOTA_BYTES = 10 * 1024 ** 4;
export const ROLE_COLORS = [
    '#5865f2',
    '#57f287',
    '#fee75c',
    '#eb459e',
    '#ed4245',
    '#3ba55d',
    '#faa81a',
    '#f47fff',
    '#00b0f4',
    '#95a5a6',
];
/** Cor de fallback do avatar, derivada do id do usuário. */
export const AVATAR_COLORS = [
    '#5865f2',
    '#57f287',
    '#eb459e',
    '#ed4245',
    '#faa81a',
    '#00b0f4',
    '#9b59b6',
    '#1abc9c',
];
//# sourceMappingURL=constants.js.map